import { Avatar, IconButton, Tooltip } from "@mui/material";
import { BasicProfile } from "protico-sdk";
import punycode from "punycode";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import CheckMark from "../../../components/CheckMark";
import ProfileModal from "../../../components/ProfileModal";
import { getFeatures } from "../../../config";
import { useAppSelector } from "../../../hooks";
import useListNft from "../../../hooks/useListNft";
import { useRouter } from "../../../hooks/useRouter";
import { ROUTES } from "../../../routes";
import { TMessage } from "../../../types/types";
import {
  decodeAssetUrl,
  decodeCurrency,
  decodeListAvailable,
  decodeListPrice,
  extractDomain,
  isAssetUrl,
  isListUrl,
} from "../../../utils/format.utils";
import Blockies from "react-blockies";
import { useBlockList } from "../../../hooks/useBlockList";


const componentDecorator = (href: any, text: any, key: any) => (
  <a
    href={href}
    key={key}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "inherit" }}
  >
    {punycode.toASCII(text)}
  </a>
);
type Prop = {
  profileData: BasicProfile | undefined;
  message: TMessage & { plaintextMessage: string };
  toAddress: string;
  scrollToBottom: () => void;
  setbuyModalstatus: any;
};
const ChatItem: React.FC<Prop> = ({
  profileData,
  message,
  toAddress,
  scrollToBottom,
  setbuyModalstatus,
}) => {
  const { t } = useTranslation();
  const { push } = useRouter();
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const { secondFontColor, fontColor } = useAppSelector(
    (state) => state.layout
  );
  const { ifHyperlink } = useAppSelector((state) => state.settings);
  const { currentAddress } = useAppSelector((state) => state.auth);
  const {
    sended,
    plaintextMessage,
    createdAt,
    fromAddress,
    cid,
    id,
    available,
    price,
    currency,
    item_status,
    chain_id,
  } = message;
  const { getDmNFT } = useListNft();
  const { domainAllowList } = useBlockList();
  const { FEATURE_NFT_LIST_ENABLED } = getFeatures();
  const msgTime = () => {
    const today = new Date();
    const messageTime = new Date(createdAt);
    if (
      today.toDateString().slice(-4) === messageTime.toDateString().slice(-4)
    ) {
      if (today.toDateString() === messageTime.toDateString()) {
        // For the today messages, we only display the time
        return messageTime.toTimeString().slice(0, 5);
      }
      // For the messages sent in the current year, we only display the date without year info
      return (
        messageTime.toLocaleString("en-GB").slice(12, 17) +
        ", " +
        messageTime.toLocaleString("en-GB").slice(0, 5)
      );
    }
    return messageTime.toLocaleString("en-GB").slice(0, 17);
  };
  const copyToClipboard = (source: string) => {
    const copiedText = document.createElement("input");
    document.body.appendChild(copiedText);
    copiedText.setAttribute("value", source);
    copiedText.select();
    document.execCommand("copy");
    document.body.removeChild(copiedText);
  };
  const [copytext, setCopytext] = useState("");

  const clickCopy = (source: string) => {
    copyToClipboard(source);
    setCopytext(t("Message_Item_Copytext"));
    setTimeout(() => {
      setCopytext("");
    }, 1300);
  };
  const messageFromMe = useMemo(() => {
    return currentAddress.toLowerCase() === message.fromAddress.toLowerCase();
  }, [message.fromAddress, currentAddress]);

  const isDomainInList = useCallback((msg: string) => {
    const domain = extractDomain(msg)?.toLowerCase() ?? '';
    if (!domain) return false;
    return domainAllowList.includes(domain);
  }, [JSON.stringify(domainAllowList)])

  // TODO: use message.sended here
  return (
    <>
      {profileModalVisible && (
        <ProfileModal
          address={fromAddress}
          uuid={fromAddress}
          setProfileModalVisible={setProfileModalVisible}
          callback={() => {
            push(ROUTES.FRIEND_PAGE);
          }}
        />
      )}
      <div className="chat-item-message-container">
        <li
          className={`chat-item-messages ${messageFromMe ? "currentUser" : ""}`}
        >
          {!messageFromMe && (
            <div className="chat-item-user-info">
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={() => {
                  setProfileModalVisible(true);
                }}
                sx={{ p: 0, margin: "0 5px" }}
                color="inherit"
              >
                {profileData.avatar_url ? (
                  <Avatar
                    alt="PFP"
                    src={profileData.avatar_url}
                    sx={{ width: 25, height: 25 }}
                  />
                ) : (
                  <Blockies
                    seed={toAddress}
                    size={10}
                    scale={2.5}
                    color="#ddd"
                    className="identicon"
                  />
                )}
              </IconButton>
            </div>
          )}
          <div
            className={`chat-item-content-container ${
              messageFromMe ? "currentUser" : ""
            }`}
          >
            {isAssetUrl(plaintextMessage) ? (
              <Tooltip
                title={
                  t("Message_Item_Visit_NFT") +
                  " " +
                  decodeAssetUrl(plaintextMessage)[1] +
                  copytext
                }
                disableInteractive
                followCursor
              >
                <div
                  className="chat-item-asset-message"
                  onClick={() => {
                    clickCopy(decodeAssetUrl(plaintextMessage)[1]);
                  }}
                >
                  <img
                    alt=""
                    className="chat-item-asset-message-image"
                    src={decodeAssetUrl(plaintextMessage)[0]}
                    onLoad={scrollToBottom}
                  />
                </div>
              </Tooltip>
            ) : isListUrl(plaintextMessage) ? (
              FEATURE_NFT_LIST_ENABLED && (
                <Tooltip
                  title={
                    t("Message_Item_Visit_NFT") +
                    " " +
                    decodeAssetUrl(plaintextMessage)[1]
                  }
                  disableInteractive
                  followCursor
                >
                  <div>
                    <div
                      className={`chat-item-list-message ${
                        decodeListAvailable(available, item_status) === 0
                          ? "available"
                          : ""
                      }`}
                      onClick={async () => {
                        if (decodeListAvailable(available, item_status) !== 0)
                          return;
                        let res = await getDmNFT(id);
                        setbuyModalstatus({
                          messageid: id,
                          status: true,
                          messageText: plaintextMessage,
                          data: res,
                          type: "dm",
                        });
                      }}
                    >
                      <img
                        alt=""
                        className="chat-item-list-message-image"
                        src={decodeAssetUrl(plaintextMessage)[0]}
                        onLoad={scrollToBottom}
                      />
                      <div className="chat-item-list-message-info">
                        <div className="chat-item-list-message-info-item-price">
                          {decodeListPrice(price, currency)}{" "}
                          {decodeCurrency(currency, chain_id)}
                        </div>
                        <div className="chat-item-list-message-info-item">
                          {decodeListAvailable(available, item_status) === 0 ? (
                            <div
                              style={{ color: "#1f8feb", fontWeight: "500" }}
                            >
                              {t("List_Nft_Available")}
                            </div>
                          ) : decodeListAvailable(available, item_status) ===
                            1 ? (
                            <div style={{ color: "#707070" }}>
                              {t("List_Nft_bought")}
                            </div>
                          ) : (
                            <div style={{ color: "#707070" }}>
                              {t("List_Nft_Expired")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Tooltip>
              )
            ) : (
              <div
                className={`chat-item-content ${
                  messageFromMe ? "currentUser" : ""
                }`}
              >
                <div
                  className="chat-item-content-text"
                  style={{ color: sended ? fontColor : "#808080" }}
                >
                  {(ifHyperlink || isDomainInList(plaintextMessage)) ? (
                    <Linkify componentDecorator={componentDecorator}>
                      {plaintextMessage}
                    </Linkify>
                  ) : (
                    plaintextMessage
                  )}
                </div>
              </div>
            )}
            <div className="chat-item-message-time-container">
              <div
                className="chat-item-time"
                style={{ color: secondFontColor }}
              >
                {sended && <CheckMark cid={cid} />}
                {!!createdAt && msgTime()}
              </div>
            </div>
          </div>
        </li>
      </div>
    </>
  );
};

export default ChatItem;
